import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import { useForm, Controller } from "react-hook-form";
import { useReportsStore } from "../../../store/reports/reportsStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAlert } from "../../../context/AlertContext";
import InfiniteDeviceSelect, { useReportDeviceIds } from "../../../components/InfiniteDeviceSelect";
import { useReportEventsStore } from "../../../store/reports/reportEventsStore";
import { allDevicesQueryOptions } from "../../../features/devices/queries";
import { useReportCompanyScope } from "../hooks/useReportCompanyScope";
import ReportEventsMap, { type SelectedEventPoint } from "../components/ReportEventsMap";
import ReportPointsTable, { type ReportTableColumn } from "../components/ReportPointsTable";
import { buildEventRows, type EventRow } from "../utils/reportPointProcessors";
import { EVENT_CATEGORIES, resolveEventCategory, type EventCategory } from "../../../utils/eventCategories";
import { exportHistoricToExcel, exportHistoricToPdf } from "../utils/exportHistoric";
import ReportActionButton from "../components/ReportActionButton";
import ReportDateRangePicker from "../components/ReportDateRangePicker";
import ReportFormatToggle from "../components/ReportFormatToggle";
import ReportSwitch from "../components/ReportSwitch";
import { toLocalInputValue } from "../utils/reportDateFormat";

const MAX_TRACKING_RANGE_DAYS = 31;
const PDF_WARN_THRESHOLD = 20000;

interface EventsFormData {
  devices: string[];
  startDate: string;
  endDate: string;
  typeReport: string;
  typeDocument: "excel" | "pdf";
  withGeocoding: boolean;
}

const isSameEvent = (selected: SelectedEventPoint, row: EventRow): boolean =>
  selected.deviceId === row.deviceId && selected.ts === row.ts && selected.eventCode === row.eventCode;

const EVENT_COLUMNS: ReportTableColumn<EventRow>[] = [
  { key: "unidad", header: "Unidad" },
  { key: "orden", header: "Orden", align: "center" },
  { key: "fecha", header: "Fecha del evento" },
  { key: "evento", header: "Evento" },
  { key: "latitud", header: "Latitud", align: "right" },
  { key: "longitud", header: "Longitud", align: "right" },
];

export default function EventsSection() {

  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);
  const generateHistoryReport = useReportsStore((state) => state.generateHistoryReport);
  const loading = useReportsStore((state) => state.loading);
  const { loadedDeviceIds, loadedCount, totalDevices } = useReportDeviceIds();

  const loadRange = useReportEventsStore((state) => state.loadRange);
  const clearEvents = useReportEventsStore((state) => state.clear);
  const groupsState = useReportEventsStore((state) => state.groups);
  const order = useReportEventsStore((state) => state.order);
  const eventsLoading = useReportEventsStore((state) => state.loading);
  const addresses = useReportEventsStore((state) => state.addresses);
  const tz = useReportEventsStore((state) => state.tz);

  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);
  const [loadAttempted, setLoadAttempted] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<SelectedEventPoint | null>(null);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm<EventsFormData>({
    mode: "onBlur",
    defaultValues: {
      devices: [],
      startDate: "",
      endDate: "",
      typeReport: "events",
      typeDocument: "pdf",
      withGeocoding: false,
    },
  });

  const { showAlert } = useAlert();

  const { companyId, disableWithoutCompany } = useReportCompanyScope(
    setValue,
    () => {
      clearEvents();
      setLoadAttempted(false);
      setSelectedEvent(null);
    },
  );
  const { data: allDevices } = useQuery({
    ...allDevicesQueryOptions(companyId),
  });

  const selectedDevices = watch("devices");
  const startDate = watch("startDate");
  const endDate = watch("endDate");

  const hasDevices = (selectedDevices?.length ?? 0) > 0;
  const hasDates = Boolean(startDate && endDate);
  const rangeDays = useMemo(() => {
    if (!hasDates) return 0;
    return (new Date(endDate).getTime() - new Date(startDate).getTime()) / (24 * 60 * 60 * 1000);
  }, [endDate, hasDates, startDate]);
  const hasValidRange = hasDates && rangeDays >= 0;
  const isWithinTrackingLimit = hasValidRange && rangeDays <= MAX_TRACKING_RANGE_DAYS;
  const filtersReadyForView = hasDevices && hasValidRange && isWithinTrackingLimit;
  const filtersReadyForReport = hasDevices && hasValidRange;

  const handleSelectAll = () => {
    if (selectedDevices?.length === loadedDeviceIds.length) {
      setValue("devices", []);
    } else {
      setValue("devices", loadedDeviceIds);
    }
  };

  const onSubmit = async (formData: EventsFormData) => {
    const result = await generateHistoryReport({
      deviceIds: formData.devices,
      startDate: formData.startDate,
      endDate: formData.endDate,
      typeReport: formData.typeReport,
      typeDocument: formData.typeDocument,
      withGeocoding: formData.withGeocoding,
    }, showAlert, () => { });

    if (result.success && result.data) {
      const extension = formData.typeDocument === "pdf" ? "pdf" : "xlsx";
      const blob = new Blob([result.data], {
        type: formData.typeDocument === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.fileName || `reporte.${extension}`;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const orderedGroups = useMemo(
    () => order.map((id) => groupsState[id]).filter(Boolean),
    [order, groupsState],
  );

  const mapGroups = useMemo(
    () =>
      orderedGroups.map((g) => ({
        deviceId: g.deviceId,
        vehicleName: g.vehicleName,
        color: g.color,
        events: g.events,
      })),
    [orderedGroups],
  );

  const presentCategories = useMemo<EventCategory[]>(() => {
    const found = new Set<EventCategory>();
    orderedGroups.forEach((g) => g.events.forEach((e) => found.add(resolveEventCategory(e.eventCode))));
    return (Object.keys(EVENT_CATEGORIES) as EventCategory[]).filter((key) => found.has(key));
  }, [orderedGroups]);

  const hasAddresses = Object.keys(addresses).length > 0;

  const tableColumns = useMemo<ReportTableColumn<EventRow>[]>(
    () =>
      hasAddresses
        ? [...EVENT_COLUMNS, { key: "direccion", header: "Dirección" }]
        : EVENT_COLUMNS,
    [hasAddresses],
  );

  const tableRows = useMemo(
    () =>
      buildEventRows(
        orderedGroups,
        tz || Intl.DateTimeFormat().resolvedOptions().timeZone,
        hasAddresses ? addresses : undefined,
      ),
    [orderedGroups, tz, hasAddresses, addresses],
  );

  const resolveNameById = useCallback((): Record<string, string> => {
    const map: Record<string, string> = {};
    (allDevices ?? []).forEach((d: any) => {
      map[d.id] = d.vehicle?.name || d.identifier || d.id;
    });
    return map;
  }, [allDevices]);

  const onViewEvents = async () => {
    const { devices, startDate, endDate, withGeocoding } = getValues();

    if (!devices || devices.length === 0) {
      showAlert({
        title: "Selecciona unidades",
        text: "Selecciona al menos un equipo para ver los eventos.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }

    if (!startDate || !endDate) {
      showAlert({
        title: "Fechas requeridas",
        text: "Selecciona la fecha de inicio y fin.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }

    const rangeDays =
      (new Date(endDate).getTime() - new Date(startDate).getTime()) / (24 * 60 * 60 * 1000);
    if (rangeDays > MAX_TRACKING_RANGE_DAYS) {
      showAlert({
        title: "Rango demasiado amplio",
        text: `Ver eventos solo muestra hasta ${MAX_TRACKING_RANGE_DAYS} días. Para rangos mayores usa "Generar Reporte".`,
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }

    const nameById = resolveNameById();

    const devicesToLoad = devices.map((id) => ({
      deviceId: id,
      vehicleName: nameById[id] || id,
    }));

    setSelectedEvent(null);
    await loadRange(
      devicesToLoad,
      new Date(startDate).toISOString(),
      new Date(endDate).toISOString(),
      { address: withGeocoding },
    );
    setLoadAttempted(true);
  };

  const onRowClick = useCallback((row: EventRow) => {
    setSelectedEvent((prev) =>
      prev && isSameEvent(prev, row)
        ? null
        : {
            deviceId: row.deviceId,
            vehicleName: row.unidad,
            ts: row.ts,
            lat: row.lat,
            lng: row.lng,
            fecha: row.fecha,
            evento: row.evento,
            eventCode: row.eventCode,
          },
    );
  }, []);

  const isRowSelected = useCallback(
    (row: EventRow) => selectedEvent != null && isSameEvent(selectedEvent, row),
    [selectedEvent],
  );

  const focusRowId = useMemo(
    () => (selectedEvent ? tableRows.find((row) => isSameEvent(selectedEvent, row))?.id ?? null : null),
    [selectedEvent, tableRows],
  );

  const clearSelectedEvent = useCallback(() => setSelectedEvent(null), []);

  const onDownload = async (typeDocument: "pdf" | "excel") => {
    if (orderedGroups.length === 0) return;

    const { startDate, endDate } = getValues();
    const extension = typeDocument === "pdf" ? "pdf" : "xlsx";
    const fileName = `events_${startDate.split("T")[0]}_${endDate.split("T")[0]}.${extension}`;
    const period = `Período: ${startDate.replace("T", " ")} al ${endDate.replace("T", " ")}`;

    const run = async () => {
      setExporting(typeDocument);
      await new Promise((r) => setTimeout(r, 50));
      try {
        const meta = { title: "Reporte de Eventos", period, fileName };
        if (typeDocument === "excel") {
          await exportHistoricToExcel(tableRows, tableColumns, meta);
        } else {
          exportHistoricToPdf(tableRows, tableColumns, meta);
        }
      } catch {
        showAlert({
          title: "Error al descargar",
          text: "No se pudo generar el archivo de eventos.",
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#2563eb",
          showCancelButton: false,
        });
      } finally {
        setExporting(null);
      }
    };

    if (typeDocument === "pdf" && tableRows.length > PDF_WARN_THRESHOLD) {
      showAlert({
        title: "El PDF será muy grande",
        text: `Hay ${tableRows.length} eventos; el PDF puede tardar y pesar mucho. Para este volumen es mejor Excel. ¿Continuar de todas formas?`,
        icon: "warning",
        confirmButtonText: "Continuar",
        confirmButtonColor: "#2563eb",
        showCancelButton: true,
        onConfirm: () => {
          run();
        },
      });
      return;
    }

    await run();
  };

  useEffect(() => {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    setValue("startDate", toLocalInputValue(firstDayOfMonth));
    setValue("endDate", toLocalInputValue(now));
  }, [setValue]);

  useEffect(() => {
    return () => {
      clearEvents();
      setLoadAttempted(false);
    };
  }, [clearEvents]);

  const hasResults = orderedGroups.length > 0;
  const totalEvents = orderedGroups.reduce((acc, g) => acc + g.events.length, 0);
  const showSkeleton = eventsLoading && totalEvents === 0;
  const showEmptyState = !hasResults && !showSkeleton;

  const hintCardClass = (active: boolean) =>
    `p-3 rounded-xl border ${
      active
        ? "bg-primary-500/10 border-primary-500/30"
        : isDarkMode
          ? "bg-dark-card border-dark-border"
          : "bg-gray-100 border-gray-200"
    }`;

  const hintTitleClass = (active: boolean) =>
    `text-xs font-medium ${active ? "text-primary-500" : themeClasses.textSecondary}`;

  const filterItems = [
    {
      ok: hasDevices,
      label: hasDevices
        ? `${selectedDevices.length} equipo(s) seleccionado(s)`
        : "Selecciona al menos un equipo",
    },
    {
      ok: hasDates,
      label: hasDates ? "Fechas de inicio y fin definidas" : "Define fecha de inicio y fin",
    },
    {
      ok: hasValidRange,
      label: hasValidRange
        ? `Rango: ${Math.max(1, Math.ceil(rangeDays))} día(s)`
        : "La fecha fin debe ser posterior a la de inicio",
    },
    {
      ok: isWithinTrackingLimit,
      label: isWithinTrackingLimit
        ? `Dentro del límite de Ver eventos (${MAX_TRACKING_RANGE_DAYS} días)`
        : `Rango mayor a ${MAX_TRACKING_RANGE_DAYS} días — usa Generar Reporte`,
    },
  ];

  return (
    <div className="h-full w-full overflow-y-auto p-2 sm:p-4">
      <form onSubmit={handleSubmit(onSubmit)} className={`flex flex-col lg:flex-row ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-3.5 gap-3 justify-between lg:items-end`}>
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4 w-full lg:w-auto">
          <div className="flex flex-col gap-1 min-w-52 w-full lg:w-auto">
            <div className="flex justify-between items-center">
              <label htmlFor="devices" className={`${themeClasses.text} text-xs`}>
                Equipos {selectedDevices?.length > 0 && `(${selectedDevices.length}/${totalDevices})`}
              </label>
              <button
                type="button"
                onClick={handleSelectAll}
                disabled={disableWithoutCompany}
                className={`text-xs px-2 py-1 rounded ${themeClasses.buttonBg} text-white hover:opacity-80 disabled:opacity-50`}
              >
                {selectedDevices?.length === loadedDeviceIds.length && loadedCount > 0 ? "Deseleccionar todos" : `Seleccionar todos (${loadedCount})`}
              </button>
            </div>
            <div>
              <Controller
                name="devices"
                control={control}
                rules={{ required: "Selecciona al menos un equipo" }}
                render={({ field }) => (
                  <InfiniteDeviceSelect
                    value={field.value}
                    onChange={field.onChange}
                    isDarkMode={isDarkMode}
                    themeClasses={themeClasses}
                  />
                )}
              />
              {errors.devices && <span className="text-red-500 text-sm">{errors.devices.message}</span>}
            </div>
          </div>
          <div className="flex flex-col gap-1 w-full lg:w-auto">
            <label className={`${themeClasses.text} text-xs`}>Rango de fechas</label>
            <input type="hidden" {...register("startDate", { required: "Fecha inicio requerida" })} />
            <input type="hidden" {...register("endDate", { required: "Fecha fin requerida" })} />
            <ReportDateRangePicker
              startValue={watch("startDate")}
              endValue={watch("endDate")}
              onChangeStart={(v) => setValue("startDate", v, { shouldValidate: true, shouldDirty: true })}
              onChangeEnd={(v) => setValue("endDate", v, { shouldValidate: true, shouldDirty: true })}
              startError={errors.startDate?.message}
              endError={errors.endDate?.message}
            />
          </div>
          <div className="flex w-full items-stretch gap-3 sm:gap-4 lg:contents">
            <div className="flex flex-col gap-1 min-w-0 flex-1 lg:flex-none">
              <label htmlFor="withGeocoding" className={`${themeClasses.text} text-xs`}>Geocodificar direcciones</label>
              <div className="flex flex-col gap-1">
                <Controller
                  name="withGeocoding"
                  control={control}
                  render={({ field }) => (
                    <div className="flex h-10 items-center">
                      <ReportSwitch id="withGeocoding" checked={Boolean(field.value)} onChange={field.onChange} ariaLabel="Geocodificar direcciones" />
                    </div>
                  )}
                />
                {errors.withGeocoding && <span className="text-red-500 text-sm">{errors.withGeocoding.message}</span>}
              </div>
            </div>
            <div className="flex flex-col gap-1 min-w-0 flex-1 lg:flex-none">
              <label className={`${themeClasses.text} text-xs`}>Formato</label>
              <Controller
                name="typeDocument"
                control={control}
                render={({ field }) => <ReportFormatToggle value={field.value} onChange={field.onChange} />}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 w-full lg:w-auto">
          <ReportActionButton
            type="button"
            variant="secondary"
            onClick={onViewEvents}
            disabled={eventsLoading || !filtersReadyForView || disableWithoutCompany}
            title={
              !filtersReadyForView && hasDevices && hasValidRange && !isWithinTrackingLimit
                ? `Ver eventos solo admite hasta ${MAX_TRACKING_RANGE_DAYS} días`
                : undefined
            }
          >
            {eventsLoading ? "Cargando..." : "Ver eventos"}
          </ReportActionButton>
          <ReportActionButton
            type="submit"
            disabled={loading || !filtersReadyForReport || disableWithoutCompany}
          >
            {loading ? "Generando..." : "Generar Reporte"}
          </ReportActionButton>
        </div>
      </form>

      {showSkeleton && (
        <div className={`mt-4 flex flex-col gap-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="inline-block w-3 h-3 rounded-full bg-primary-500 animate-pulse" />
              <span className={`h-3 w-28 rounded animate-pulse ${isDarkMode ? "bg-white/10" : "bg-black/10"}`} />
            </div>
            <span className={`h-3 w-16 rounded animate-pulse ${isDarkMode ? "bg-white/10" : "bg-black/10"}`} />
          </div>

          <div
            className={`relative w-full overflow-hidden rounded-lg ${isDarkMode ? "bg-white/5" : "bg-black/5"}`}
            style={{ height: 420 }}
          >
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
              <div className="relative flex items-center justify-center w-20 h-20">
                <span className="absolute inline-flex w-full h-full rounded-full bg-primary-500/20 animate-ping" />
                <span className="absolute inline-flex w-12 h-12 rounded-full border-4 border-primary-500 border-t-transparent animate-spin" />
                <span className="relative inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary-500/15">
                  <i className="ri-alarm-warning-line text-primary-500 text-2xl animate-bounce" />
                </span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <span className={`text-sm font-semibold ${themeClasses.text}`}>Buscando eventos…</span>
                <span className={`text-xs ${themeClasses.text} opacity-70`}>
                  Obteniendo eventos del rango seleccionado
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasResults && !showSkeleton && (
        <div className={`mt-4 flex flex-col gap-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              {orderedGroups.map((g) => (
                <div key={g.deviceId} className="flex items-center gap-2">
                  <span
                    className="inline-block w-3 h-3 rounded-full"
                    style={{ backgroundColor: g.color }}
                  />
                  <span className={`text-sm ${themeClasses.text}`} title={g.error ?? undefined}>
                    {g.vehicleName}
                    {g.loading
                      ? " (cargando...)"
                      : g.error
                        ? " (error)"
                        : ` (${g.events.length.toLocaleString("es-MX")})`}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm ${themeClasses.text}`}>
                {totalEvents.toLocaleString("es-MX")} eventos
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => onDownload("pdf")}
                  disabled={exporting !== null}
                  className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} disabled:opacity-50`}
                >
                  {exporting === "pdf" ? "Generando..." : "Descargar PDF"}
                </button>
                <button
                  type="button"
                  onClick={() => onDownload("excel")}
                  disabled={exporting !== null}
                  className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} disabled:opacity-50`}
                >
                  {exporting === "excel" ? "Generando..." : "Descargar Excel"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    clearEvents();
                    setLoadAttempted(false);
                    setSelectedEvent(null);
                  }}
                  className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} ${themeClasses.buttonText} hover:opacity-80`}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>

          {orderedGroups.some((g) => g.error) && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 flex flex-col gap-1">
              {orderedGroups
                .filter((g) => g.error)
                .map((g) => (
                  <p key={g.deviceId} className="text-[11px] text-red-500 break-all">
                    <span className="font-semibold">{g.vehicleName}:</span> {g.error}
                  </p>
                ))}
            </div>
          )}

          <p className={`text-xs ${themeClasses.textSecondary}`}>
            Pasa el cursor sobre la ruta para ver el evento de cada punto, haz clic en la ruta para fijar el detalle, o haz clic en una fila de la tabla para ubicarla en el mapa.
          </p>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className={`text-[11px] font-medium ${themeClasses.text}`}>
              Eventos encontrados:
            </span>
            {presentCategories.map((key) => (
              <span key={key} className={`text-[11px] ${themeClasses.textSecondary}`}>
                {EVENT_CATEGORIES[key].label}
              </span>
            ))}
            {orderedGroups.length === 1 && (
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-4 h-1 rounded-full bg-slate-400" />
                <span className={`text-[11px] ${themeClasses.textSecondary}`}>Recorrido</span>
              </div>
            )}
          </div>

          <div className={`-mx-3 sm:-mx-4 px-3 sm:px-4 py-2 ${themeClasses.cardBg} border-b ${themeClasses.cardBorder}`}>
            <ReportEventsMap
              groups={mapGroups}
              tz={tz || undefined}
              selected={selectedEvent}
              onSelect={setSelectedEvent}
              onClearSelected={clearSelectedEvent}
            />
          </div>

          <ReportPointsTable
            data={tableRows}
            columns={tableColumns}
            isDarkMode={isDarkMode}
            emptyText="No se encontraron eventos en el rango seleccionado."
            onRowClick={onRowClick}
            isRowSelected={isRowSelected}
            focusRowId={focusRowId}
          />
        </div>
      )}

      {showEmptyState && (
        <div className={`mt-4 flex flex-col gap-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div
            className={`relative w-full overflow-hidden rounded-xl border border-dashed ${themeClasses.cardBorder} ${isDarkMode ? "bg-white/5" : "bg-black/[0.02]"}`}
            style={{ minHeight: 320 }}
          >
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
              <span className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${isDarkMode ? "bg-primary-500/15" : "bg-primary-500/10"}`}>
                <i className="ri-alarm-warning-line text-primary-500 text-2xl" />
              </span>
              <div>
                <p className={`text-sm font-semibold ${themeClasses.text}`}>
                  Mapa y tabla de eventos
                </p>
                <p className={`text-xs mt-1 max-w-md ${themeClasses.textSecondary}`}>
                  Configura los filtros y usa <strong>Ver eventos</strong> para visualizar dónde ocurrió cada evento en el mapa y en la tabla.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className={hintCardClass(filtersReadyForView)}>
              <div className="flex items-start space-x-2">
                <i className={`ri-map-pin-line text-base ${filtersReadyForView ? "text-primary-500" : themeClasses.textSecondary}`} />
                <div className="flex-1 min-w-0">
                  <p className={hintTitleClass(filtersReadyForView)}>Ver eventos</p>
                  <p className={`text-sm font-semibold ${themeClasses.text} mt-0.5`}>
                    Hasta {MAX_TRACKING_RANGE_DAYS} días (1 mes)
                  </p>
                  <p className={`text-xs mt-1 ${themeClasses.textSecondary}`}>
                    Muestra cada evento como marcador en el mapa y en una tabla con su detalle. Ideal para consultas rápidas.
                  </p>
                </div>
              </div>
            </div>

            <div className={hintCardClass(!isWithinTrackingLimit && hasValidRange)}>
              <div className="flex items-start space-x-2">
                <i className={`ri-file-download-line text-base ${!isWithinTrackingLimit && hasValidRange ? "text-primary-500" : themeClasses.textSecondary}`} />
                <div className="flex-1 min-w-0">
                  <p className={hintTitleClass(!isWithinTrackingLimit && hasValidRange)}>Generar reporte</p>
                  <p className={`text-sm font-semibold ${themeClasses.text} mt-0.5`}>
                    Sin límite de rango
                  </p>
                  <p className={`text-xs mt-1 ${themeClasses.textSecondary}`}>
                    Para rangos mayores a 1 mes el reporte se genera en segundo plano. Te avisaremos cuando esté listo para descargar.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className={hintCardClass(filterItems.every((item) => item.ok))}>
            <div className="flex items-start space-x-2">
              <i className={`ri-filter-3-line text-base ${filterItems.every((item) => item.ok) ? "text-primary-500" : themeClasses.textSecondary}`} />
              <div className="flex-1 min-w-0">
                <p className={hintTitleClass(filterItems.every((item) => item.ok))}>
                  {filterItems.every((item) => item.ok)
                    ? "Filtros listos"
                    : "Completa los filtros"}
                </p>
                <ul className="mt-2 space-y-1.5">
                  {filterItems.map((item) => (
                    <li key={item.label} className="flex items-start gap-2 text-xs">
                      <i
                        className={`mt-0.5 ${
                          item.ok ? "ri-checkbox-circle-fill text-primary-500" : "ri-close-circle-line text-red-400"
                        }`}
                      />
                      <span className={item.ok ? themeClasses.text : themeClasses.textSecondary}>
                        {item.label}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {loadAttempted && !eventsLoading && (
            <div className={`p-3 rounded-xl border ${isDarkMode ? "bg-dark-card border-dark-border" : "bg-gray-100 border-gray-200"}`}>
              <div className="flex items-start space-x-2">
                <i className={`ri-information-line text-base ${themeClasses.textSecondary}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${themeClasses.textSecondary}`}>Sin resultados</p>
                  <p className={`text-sm ${themeClasses.text} mt-0.5`}>
                    No se encontraron eventos en el rango seleccionado. Verifica equipos, fechas y vuelve a intentar, o usa <strong>Generar reporte</strong> para un análisis más amplio.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
